import React, { Component } from 'react';
import { connect } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import moment from 'moment';

import axios from '../../../axios-url';
import * as actions from '../../../store/actions/index';
import { ORDER_TYPES } from '../../Layout/POSSidebar';

import FloorPlan from './FloorPlan';
import ActionPanel from './ActionPanel';
// eslint-disable-next-line no-unused-vars -- kept for the parked Pending Items card/modal below
import PendingItemsModal from './PendingItemsModal';
import ResourceEngagementModal from './ResourceEngagementModal';
import OrdersInProgressModal from './OrdersInProgressModal';
import PendingOrdersModal from './PendingOrdersModal';
import { STATUS } from './mockFloorPlanData';
import { mapActionsToItems, itemsForRequest, buildMarkServedPayload, buildSendToKotPayload, SERVED_STATUS_ID } from './kotActions';
import {
    getDecisionStatusIds,
    groupPendingSpecialOrders,
    buildApprovePayload,
    buildRejectPayload,
} from './specialOrderApproval';

import './CaptainDashboard.scss';


// Inclusive day-granularity range check for the Pending Items modal's date
// filter — 'to' falls back to 'from' so a single date picked (the default,
// today only) still matches everything from that one day.
const isWithinDateRange = (dateValue, from, to) => {
    if (!from) return true;
    if (!dateValue) return false;
    const day = moment(dateValue);
    if (!day.isValid()) return false;
    return day.isBetween(
        moment(from).startOf('day'),
        moment(to || from).endOf('day'),
        undefined,
        '[]'
    );
};

class CaptainDashboard extends Component {

    state = {
        search: '',
        selectedTableId: null,
        selectedOtherOrderId: null,
        runningOrders: [],
        actionRows: [],
        selectedKotItemIds: [],
        pendingItemsModalOpen: false,
        selectedPendingItemIds: [],
        // Pending Items modal's delivery-date filter — [from, to], both the
        // same Date instance by default so it opens scoped to just today.
        pendingItemsDateRange: [new Date(), new Date()],
        resourceModalOpen: false,
        ordersModalOpen: false,
        // Special-order approval. resStatusMaster is fetched rather than
        // assumed because the KOT status ids have already been renumbered once
        // (CONFIRMED BY CUSTOMER is 15, CONFIRMED is 10 — the reverse of what
        // kotActions.js still hardcodes); see specialOrderApproval.js.
        resStatusMaster: null,
        // The order currently being written, so its row can show a spinner and
        // every other Approve/Reject is locked out until it settles.
        decidingRequestId: null,
        decisionError: null,
        pendingOrdersModalOpen: false,
        // True from the moment a refresh is dispatched until new rows land.
        // The modal says so rather than presenting rows that may already be
        // out of date as if they were current.
        kotRefreshing: false,
        // Orders decided in this session, dropped from the pending list the
        // instant the write succeeds. Without this the order stays on screen
        // until the refetch round-trips, and a captain can approve it twice.
        // Cleared once a refresh confirms they are genuinely gone.
        decidedRequestIds: [],
    };

    componentDidMount() {
        // Same two calls TableSelection.js's own table grid is built from —
        // GetresTableList for the table master list, GetrestableOrderNum for
        // which of them currently have a running order. See API_MAPPING.md
        // for what a Captain Dashboard still can't get from these (zone,
        // QR-source flag, table-linked bookings).
        this.props.getMasterData('res/GetresTableList?companyId=' + this.props.loggedUserDetail.CompanyId);
        this.fetchRunningOrders();

        // KOT/item-level action data — same GetresActionAllList + MenuItem
        // pair Waiter.js reads, so the selected table's items and their
        // Pending/Preparing/Ready status match what the kitchen/waiter
        // screens already show for that same order.
        this.refreshKotActions();
        this._stopKotLive = this.props.startKotActionsLive();
        this.fetchResStatusMaster();
        this.props.getMasterKeywordList('MenuItem');
        this.props.getMasterKeywordList('Modifier');
        // this.props.getMasterKeywordList('MenuItem');

        // Resolves table.waiter (an id, see AddTableMaster.js) into a display
        // name for the Resource Engagement modal's Waiters tab.
        this.props.getMasterKeywordList('Waiter');

        // Resolves a KOT item's kitchen id into a display name for the same
        // modal's Kitchen tab — the real Kitchen master RestaurantOrder.js
        // already reads for KOT print grouping (see printBuilders.js's
        // buildKotPrintData).
        this.props.getMasterKeywordList('Kitchen');
    }

    componentWillUnmount() {
        if (this._stopKotLive) this._stopKotLive();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.tableLists !== this.props.tableLists && Array.isArray(this.props.tableLists)) {
            const rows = this.props.tableLists;

            this.setState((prev) => {
                const ids = getDecisionStatusIds(prev.resStatusMaster);
                // A decided order is only forgotten once the server agrees it
                // has left the pending status. Dropping the ids unconditionally
                // would make an order reappear whenever a refresh raced the
                // write and came back with the old rows still in it.
                const stillPending = new Set(
                    rows
                        .filter(
                            (r) =>
                                Number(r.actiontype) === 1 &&
                                Number(r.statusId) === Number(ids.confirmedByCustomer)
                        )
                        .map((r) => String(r.requestid))
                );

                return {
                    actionRows: rows,
                    kotRefreshing: false,
                    decidedRequestIds: prev.decidedRequestIds.filter((id) =>
                        stillPending.has(String(id))
                    ),
                };
            });
        }
    }

    fetchRunningOrders = () => {
        axios.get("/master/api/res/GetrestableOrderNum?BranchId=00000000-0000-0000-0000-000000000001" + "&CompanyId=" + this.props.loggedUserDetail.CompanyId)
            .then(response => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    this.setState({ runningOrders: response.data });
                }
            })
            .catch(err => console.error('Failed to fetch running orders', err));
    };

    // Reloads the whole action list (res/GetresActionAllList). Between these
    // calls the list is kept current by startKotActionsLive.
    refreshKotActions = () => {
        this.setState({ kotRefreshing: true });
        this.props.loadKotActions();
    };

    onTableSelect = (table) => {
        // Only one selection drives the detail panel at a time.
        this.setState({ selectedTableId: table.id, selectedOtherOrderId: null });
    };

    // Direct axios, not getMasterData: that action caches every master in
    // localStorage indefinitely, and a stale copy of THIS list would write the
    // wrong statusId onto an approval. Same reasoning as
    // SpecialOrderDetails.fetchResStatusMaster.
    fetchResStatusMaster = () => {
        axios.get(
            '/master/api/res/GetresActionStatusflow?companyId=' + this.props.loggedUserDetail.CompanyId
        )
            .then((response) => {
                this.setState({
                    resStatusMaster: Array.isArray(response.data) ? response.data : [],
                });
            })
            // [] rather than leaving it null: getDecisionStatusIds then falls
            // back to the known-good ids and the card still works.
            .catch(() => this.setState({ resStatusMaster: [] }));
    };

    // Refreshes on open rather than trusting whatever landed at mount: the
    // dashboard can sit on a screen for hours, and a captain must never
    // approve an order another device already dealt with.
    openPendingOrdersModal = () => {
        this.setState({ pendingOrdersModalOpen: true, decisionError: null });
        this.refreshKotActions();
    };

    closePendingOrdersModal = () =>
        this.setState({ pendingOrdersModalOpen: false, decisionError: null });

    getPendingSpecialOrders = () => {
        const { actionRows, resStatusMaster, decidedRequestIds } = this.state;
        const ids = getDecisionStatusIds(resStatusMaster);
        const groups = groupPendingSpecialOrders(actionRows, this.props.MenuItem, ids.confirmedByCustomer);

        if (!decidedRequestIds.length) return groups;
        const decided = new Set(decidedRequestIds.map(String));
        return groups.filter((g) => !decided.has(String(g.requestId)));
    };

    // Approve and reject differ only in which statuses they write, so both go
    // through here: KOT lines and the order row move together in ONE
    // Restaurant_Action_Update post, so an order cannot end up half-decided.
    decideSpecialOrder = async (group, kind) => {
        if (!group || this.state.decidingRequestId) return;

        const ids = getDecisionStatusIds(this.state.resStatusMaster);
        const payload = kind === 'reject'
            ? buildRejectPayload(group, ids)
            : buildApprovePayload(group, ids);
        if (!payload.length) return;

        this.setState({ decidingRequestId: group.requestId, decisionError: null });

        try {
            await axios.post('/master/api/UpdateDynamicData?model=Restaurant_Action_Update', {
                Key: JSON.stringify(payload),
            });
            // Hidden immediately, before the refetch: leaving it on screen
            // until the round-trip completes is what lets a second click
            // through on an order that is already decided.
            this.setState((prev) => ({
                decidingRequestId: null,
                decidedRequestIds: [...prev.decidedRequestIds, group.requestId],
            }));
            this.refreshKotActions();
        } catch (err) {
            console.error(`Failed to ${kind} special order`, err);
            this.setState({
                decidingRequestId: null,
                decisionError: `Could not ${kind === 'reject' ? 'reject' : 'approve'} ${group.orderNo}. Please try again.`,
            });
        }
    };

    approveSpecialOrder = (group) => this.decideSpecialOrder(group, 'approve');

    rejectSpecialOrder = (group) => this.decideSpecialOrder(group, 'reject');

    onSelectOtherOrder = (requestId) => {
        this.setState({ selectedOtherOrderId: requestId, selectedTableId: null });
    };

    closeDetail = () => {
        this.setState({ selectedTableId: null, selectedOtherOrderId: null });
    };

    // Takeaway/Delivery running orders — sourced from the same
    // GetresActionAllList rows the KOT items section already uses, so
    // there's no separate employee-scoped fetch (GetRequestListGen, the
    // other "list of orders" endpoint in this codebase, only returns the
    // logged-in user's own orders — no good for a shared dashboard). Grouped
    // by requestid, labeled with `ptsjrsr` (the human-readable order number
    // on that row, e.g. "ROD-202607-252"). "Still running" is approximated
    // the same way Waiter.js's removeAlreadyCompletedOrders does: an order
    // drops off once every one of its rows has reached a ready/served
    // statusId — there's no confirmed order-level closed flag on this
    // endpoint (see API_MAPPING.md).
    getOtherOrders = () => {
        const { actionRows } = this.state;
        const groups = {};

        actionRows.forEach(r => {
            const type = String(r.ordertype);
            if (type !== ORDER_TYPES.TAKEAWAY && type !== ORDER_TYPES.DELIVERY) return;
            if (!r.requestid) return;

            if (!groups[r.requestid]) {
                groups[r.requestid] = {
                    requestId: r.requestid,
                    label: r.ptsjrsr || r.requestid,
                    rows: [],
                };
            }
            groups[r.requestid].rows.push(r);
        });

        // Drops off the list once every item is actually served, not merely
        // prepared — a "prepared but not yet served" order still needs the
        // captain's attention, so it has to stay pickable.
        return Object.values(groups).filter(
            g => !g.rows.every(r => Number(r.statusId) === SERVED_STATUS_ID)
        );
    };

    // Item-level KOT rows for the table currently open in the detail panel
    // only — Waiter.js shows every open order at once, this scopes the same
    // mapping down to the one order behind the selected table.
    getKotItems = (table) => {
        if (!table || !table.runningOrder) return [];
        const { actionRows, selectedKotItemIds } = this.state;
        const mapped = mapActionsToItems(actionRows, this.props.MenuItem);
        const scoped = itemsForRequest(mapped, table.runningOrder.requestId);
        return scoped.map(item => ({
            ...item,
            selected: item.status === 'served' || selectedKotItemIds.includes(item.id),
        }));
    };

    toggleKotItemSelected = (itemId, checked) => {
        this.setState(prev => ({
            selectedKotItemIds: checked
                ? [...prev.selectedKotItemIds, itemId]
                : prev.selectedKotItemIds.filter(id => id !== itemId),
        }));
    };

    // Same Restaurant_Action_Update write Waiter.js's handleCollectiveReady
    // does. Only 'prepared' items are ever eligible here — a captain/waiter
    // can mark something the kitchen has finished as served, nothing else;
    // 'preparing' items never get a checkbox in ActionPanel to begin with,
    // but the filter is repeated here too so a stale selection can't slip
    // one through.
    markKotItemsServed = async (items) => {
        const toSubmit = items.filter(it => it.selected && it.status === 'prepared');
        if (toSubmit.length === 0) return;

        try {
            await axios.post('/master/api/UpdateDynamicData?model=Restaurant_Action_Update', {
                Key: JSON.stringify(buildMarkServedPayload(toSubmit)),
            });
            this.setState({ selectedKotItemIds: [] });
            this.refreshKotActions();
        } catch (err) {
            console.error('Failed to mark KOT items served', err);
        }
    };

    // Closes the whole order — a fresh REQUESTACTIONS row with actiontype 0
    // (ORDER) and statusId 999, the same placeholder "done" status
    // markKotItemsServed uses (a real ActionId 0/CLOSED RESSTATUS row isn't
    // confirmed to exist — see API_MAPPING.md). This is its own row, not an
    // update of one of the order's existing item-level KOT rows: it only
    // borrows tablenumber/tokennumber/ordertype from one of those (if any
    // exist yet) for context, and actionnum is this order's next action
    // number, not that row's.
    // Frees the table back up on the Table Master (status 1 = Available, see
    // AddTableMaster.js statusOptions — same values TableSelection.js's
    // resolveTableStatus reads) now that its order is closed. Looks up the
    // raw GetresTableList record for this tableId, since the update replaces
    // the whole row and the tile object (getTiles) only carries a subset of
    // its fields.
    updateTableMasterStatus = (tableId, status) => {
        if (!tableId) return;
        const rawTable = (this.props.tables || []).find(t => t.tableId === tableId);
        if (!rawTable) return;
        this.props.processDynamicData('restable', 'edit', { ...rawTable, status }, '');
        setTimeout(() => {
            localStorage.removeItem('MasterData-GetresTableList');
            this.props.getMasterData('res/GetresTableList?companyId=' + this.props.loggedUserDetail.CompanyId);
        }, 2000);
    };

    closeOrder = async (table) => {
        const requestId = table && table.runningOrder && table.runningOrder.requestId;
        if (!requestId) return;

        // Case-insensitive — see itemsForRequest in kotActions.js for why
        // (requestId here can come from the dine-in GetrestableOrderNum path).
        const target = String(requestId).toLowerCase();
        const relatedRows = this.state.actionRows.filter(r => String(r.requestid).toLowerCase() === target);
        const sample = relatedRows[0] || {};

        const closeRow = {
            requestid: requestId.toUpperCase(),
            actiontype: 0,
            statusId: SERVED_STATUS_ID,
            actionnum: relatedRows.length + 1,
            tablenumber: sample.tablenumber || null,
            tokennumber: sample.tokennumber || null,
            kotnumber: null,
            menuId: null,
            menuQty: null,
            ordertype: sample.ordertype || null,
            createddate: new Date().toISOString(),
        };

        try {
            await axios.post('/master/api/UpdateDynamicData?model=Restaurant_Action_Update', {
                Key: JSON.stringify([closeRow]),
            });
            if (table.tableId) {
                this.updateTableMasterStatus(table.tableId, 1);
            }
            this.closeDetail();
            this.fetchRunningOrders();
            this.refreshKotActions();
        } catch (err) {
            console.error('Failed to close order', err);
        }
    };

    openPendingItemsModal = () => this.setState({ pendingItemsModalOpen: true });

    closePendingItemsModal = () => this.setState({ pendingItemsModalOpen: false, selectedPendingItemIds: [] });

    onPendingItemsDateRangeChange = (pendingItemsDateRange) => this.setState({ pendingItemsDateRange });

    togglePendingItemSelected = (itemId, checked) => {
        this.setState(prev => ({
            selectedPendingItemIds: checked
                ? [...prev.selectedPendingItemIds, itemId]
                : prev.selectedPendingItemIds.filter(id => id !== itemId),
        }));
    };

    // Select/clear every item in one order at once — the checkbox at the top
    // of that order's expanded item list.
    togglePendingItemGroupSelected = (group, checked) => {
        const ids = group.items.map(it => it.id);
        this.setState(prev => ({
            selectedPendingItemIds: checked
                ? Array.from(new Set([...prev.selectedPendingItemIds, ...ids]))
                : prev.selectedPendingItemIds.filter(id => !ids.includes(id)),
        }));
    };

    // Same, but across every order currently listed in the modal — the
    // checkbox at the very top.
    toggleAllPendingItemsSelected = (groups, checked) => {
        const ids = groups.flatMap(g => g.items.map(it => it.id));
        this.setState(prev => ({
            selectedPendingItemIds: checked
                ? Array.from(new Set([...prev.selectedPendingItemIds, ...ids]))
                : prev.selectedPendingItemIds.filter(id => !ids.includes(id)),
        }));
    };

    // Pending items waiting on the captain to dispatch to the kitchen — every
    // GetresActionAllList item at statusId 10 ("confirmed by customer"),
    // grouped by requestid into one collapsible entry per order. Scoped to
    // the modal's own delivery-date range filter (defaults to today only).
    getPendingItemGroups = () => {
        const { actionRows, selectedPendingItemIds, pendingItemsDateRange } = this.state;
        const [dateFrom, dateTo] = pendingItemsDateRange;
        const mapped = mapActionsToItems(actionRows, this.props.MenuItem);
        const confirmed = mapped.filter(it =>
            it.status === 'confirmed' && isWithinDateRange(it.deliveryDate, dateFrom, dateTo)
        );

        const groups = {};
        confirmed.forEach(item => {
            if (!groups[item.requestId]) {
                groups[item.requestId] = {
                    requestId: item.requestId,
                    label: item.ptsjrsr || item.requestId,
                    tableNo: item.tableNo,
                    items: [],
                };
            }
            groups[item.requestId].items.push({
                ...item,
                selected: selectedPendingItemIds.includes(item.id),
            });
        });

        return Object.values(groups);
    };

    // Same Restaurant_Action_Update write as markKotItemsServed, dispatching
    // whichever of this order's confirmed items are checked: statusId
    // 10 -> 11 ("Sent to KOT").
    sendPendingItemsToKitchen = async (items) => {
        const toSubmit = items.filter(it => it.selected);
        if (toSubmit.length === 0) return;

        try {
            await axios.post('/master/api/UpdateDynamicData?model=Restaurant_Action_Update', {
                Key: JSON.stringify(buildSendToKotPayload(toSubmit)),
            });
            this.setState(prev => ({
                selectedPendingItemIds: prev.selectedPendingItemIds.filter(
                    id => !toSubmit.some(it => it.id === id)
                ),
            }));
            this.refreshKotActions();
        } catch (err) {
            console.error('Failed to send pending items to kitchen', err);
        }
    };

    openResourceModal = () => this.setState({ resourceModalOpen: true });

    closeResourceModal = () => this.setState({ resourceModalOpen: false });

    openOrdersModal = () => this.setState({ ordersModalOpen: true });

    closeOrdersModal = () => this.setState({ ordersModalOpen: false });

    // Every running order regardless of type — dine-in tiles already sitting
    // at STATUS.PROGRESS, plus the Takeaway/Delivery groups from
    // getOtherOrders — flattened into one list for the "Orders in Progress"
    // modal. Each row carries a `table` shaped exactly like the tile
    // ActionPanel already knows how to close/view (findSelectedTable builds
    // the same synthetic shape for a Takeaway/Delivery pick), so the modal
    // can hand it straight back to closeOrder/viewOrder unchanged.
    getAllRunningOrders = (tiles, otherOrders) => {
        const { actionRows } = this.state;
        const mapped = mapActionsToItems(actionRows, this.props.MenuItem);

        const dineIn = tiles
            .filter(t => t.status === STATUS.PROGRESS && t.runningOrder)
            .map(t => {
                const requestId = t.runningOrder.requestId;
                return {
                    key: `table-${t.id}`,
                    typeLabel: 'Dine-in',
                    typeClass: 'dinein',
                    label: `Table ${t.id}`,
                    orderNo: t.runningOrder.ordereNumber,
                    duration: t.runningOrder.duration,
                    amount: t.runningOrder.amount,
                    itemCount: requestId ? itemsForRequest(mapped, requestId).length : null,
                    table: t,
                };
            });

        const others = otherOrders.map(o => {
            const firstType = o.rows[0] && String(o.rows[0].ordertype);
            const isDelivery = firstType === ORDER_TYPES.DELIVERY;
            return {
                key: `order-${o.requestId}`,
                typeLabel: isDelivery ? 'Delivery' : 'Takeaway',
                typeClass: isDelivery ? 'delivery' : 'takeaway',
                label: o.label,
                orderNo: null,
                duration: null,
                amount: null,
                itemCount: itemsForRequest(mapped, o.requestId).length,
                table: {
                    id: o.label,
                    status: STATUS.PROGRESS,
                    runningOrder: { requestId: o.requestId, ordereNumber: o.label },
                },
            };
        });

        return [...dineIn, ...others];
    };

    // Live KOT tickets for the Resource Engagement modal's Kitchen tab — same
    // 'preparing' bucket (statusId 11, sent to KOT but not yet ready) the
    // Kitchen Items list elsewhere on this page already uses, restricted to
    // actiontype === 1 the same way KOTDashboard's own Kitchen tab does.
    // Grouped by kitchen (menuitemkitchenid/kitchenid on the action row,
    // resolved to a name via the real Kitchen master keyword list — same
    // field RestaurantOrder.js's own KOT print grouping uses, see
    // printBuilders.js's buildKotPrintData), then within each kitchen by
    // kotStationKey (KOTDashboard.js's own logic: kotnumber, or a
    // requestid+kitchen fallback).
    getLiveKotGroups = () => {
        const { actionRows } = this.state;
        const { MenuItem, Kitchen: kitchenOptions = [] } = this.props;
        const mapped = mapActionsToItems(actionRows, MenuItem);
        const live = mapped.filter(it => it.actiontype === 1 && it.status === 'preparing');

        const resolveKitchenName = (id) => {
            const match = kitchenOptions.find(k =>
                String(k.value) === String(id) || String(k.id) === String(id)
            );
            return match ? (match.label || match.name || match.text) : null;
        };

        const kitchens = {};
        live.forEach(it => {
            const kitchenId = it.menuitemkitchenid || it.kitchenid || 'unassigned';
            if (!kitchens[kitchenId]) {
                kitchens[kitchenId] = {
                    kitchenId,
                    kitchenName: kitchenId === 'unassigned'
                        ? 'Unassigned'
                        : (resolveKitchenName(kitchenId) || `Kitchen ${kitchenId}`),
                    kots: {},
                };
            }

            const kotKey = it.kotnumber || `${it.requestId}-${kitchenId}`;
            if (!kitchens[kitchenId].kots[kotKey]) {
                kitchens[kitchenId].kots[kotKey] = {
                    key: kotKey,
                    kotNo: it.kotNo,
                    tableNo: it.tableNo,
                    earliest: it.createddate,
                    items: [],
                };
            }
            const kot = kitchens[kitchenId].kots[kotKey];
            if (it.createddate && (!kot.earliest || new Date(it.createddate) < new Date(kot.earliest))) {
                kot.earliest = it.createddate;
            }
            kot.items.push(it);
        });

        return Object.values(kitchens).map(k => ({ ...k, kots: Object.values(k.kots) }));
    };

    // Waiters tab data — the table master's own static `waiter` assignment
    // (TableMaster/AddTableMaster.js's `waiter` field, resolved to a name via
    // the 'Waiter' keyword list) paired with that table's running order, if
    // any. This is the table's default assigned waiter, not a confirmed
    // per-order one — the per-order Waiter field lives inside that single
    // order's own RequestAttributesDetails and isn't readable in bulk. See
    // API_MAPPING.md.
    getWaiterAssignments = () => {
        const { tables = [], Waiter: waiterOptions = [] } = this.props;
        const { runningOrders } = this.state;

        const runningByTableNo = {};
        runningOrders.forEach(order => {
            if (order.tableNumber) runningByTableNo[order.tableNumber] = order;
        });

        const resolveWaiterName = (id) => {
            const match = waiterOptions.find(w =>
                String(w.value) === String(id) || String(w.id) === String(id)
            );
            return match ? (match.label || match.name || match.text) : null;
        };

        const groups = {};
        (tables || []).forEach(t => {
            const runningOrder = runningByTableNo[t.tableNo];
            if (!runningOrder) return;

            const waiterId = t.waiter || null;
            const key = waiterId || '__unassigned';
            if (!groups[key]) {
                groups[key] = {
                    waiterId,
                    waiterName: waiterId ? (resolveWaiterName(waiterId) || `Waiter ${waiterId}`) : 'Unassigned',
                    tables: [],
                };
            }
            groups[key].tables.push({
                tableNo: t.tableNo,
                orderNo: runningOrder.ordereNumber,
                duration: runningOrder.duration,
            });
        });

        return Object.values(groups);
    };

    // Real GetresTableList rows only carry tableId/tableNo/capacity/status/
    // waiter — no zone field, so tiles are always 'square' and ungrouped
    // until that gap (API_MAPPING.md §2/§3) is closed. Status is only ever
    // Available/In Progress, derived the same way TableSelection.js's
    // runningOrderMap does: does GetrestableOrderNum have a row for this
    // table's tableNo.
    getTiles = () => {
        const { tables = [] } = this.props;
        const { runningOrders, search } = this.state;
        const term = search.trim().toLowerCase();

        const runningByTableNo = {};
        runningOrders.forEach(order => {
            if (order.tableNumber) runningByTableNo[order.tableNumber] = order;
        });

        return (tables || [])
            .filter(t => t.tableNo && t.tableNo.toLowerCase().includes(term))
            .map(t => {
                const runningOrder = runningByTableNo[t.tableNo] || null;
                return {
                    id: t.tableNo,
                    tableId: t.tableId,
                    shape: 'square',
                    status: runningOrder ? STATUS.PROGRESS : STATUS.AVAILABLE,
                    runningOrder,
                };
            });
    };

    // A "table" selected from the floor plan grid, or a synthetic stand-in
    // for a Takeaway/Delivery order picked from the dropdown — either way,
    // ActionPanel only ever needs table.status/table.runningOrder, so both
    // shapes reuse it (and the KOT items/Close Order logic under it)
    // unchanged.
    findSelectedTable = (tiles, otherOrders) => {
        const { selectedTableId, selectedOtherOrderId } = this.state;

        if (selectedTableId) {
            return tiles.find(t => t.id === selectedTableId) || null;
        }

        if (selectedOtherOrderId) {
            const order = otherOrders.find(o => o.requestId === selectedOtherOrderId);
            if (!order) return null;
            return {
                id: order.label,
                status: STATUS.PROGRESS,
                runningOrder: { requestId: order.requestId, ordereNumber: order.label },
            };
        }

        return null;
    };

    // ordersInProgress now counts the same list the "Orders in Progress"
    // modal shows (dine-in + Takeaway/Delivery) — previously this only
    // counted dine-in tiles, so the card undercounted whenever a
    // Takeaway/Delivery order was running with no table attached to it.
    getSummary = (tiles, pendingItemGroups, allRunningOrders, pendingSpecialOrders = []) => ({
        // Booking Requests still has no backing API — see API_MAPPING.md §1.
        bookingRequests: 0,
        // Still computed while its card is parked, so restoring the card needs
        // no change here.
        pendingItems: pendingItemGroups.length,
        pendingOrders: pendingSpecialOrders.length,
        ordersInProgress: allRunningOrders.length,
        totalTables: tiles.length,
    });

    viewOrder = (requestId) => {
        if (requestId) this.props.history.push('/pos/order/' + requestId);
    };

    renderSummaryCard(icon, value, title, colorClass, onClick) {
        return (
            <div
                className={`captain-summary-card captain-summary-card--${colorClass}${onClick ? ' captain-summary-card--clickable' : ''}`}
                onClick={onClick}
            >
                <div className="captain-summary-icon">
                    <i className={`pi ${icon}`} />
                </div>
                <div className="captain-summary-body">
                    <div className="captain-summary-value">{value}</div>
                    <div className="captain-summary-title">{title}</div>
                    <button type="button" className="captain-summary-link">
                        View all <i className="pi pi-angle-right" />
                    </button>
                </div>
            </div>
        );
    }

    render() {
        // eslint-disable-next-line no-unused-vars -- pendingItemsModalOpen feeds the parked modal below
        const { search, selectedOtherOrderId, pendingItemsModalOpen } = this.state;
        const tiles = this.getTiles();
        const otherOrders = this.getOtherOrders();
        const pendingItemGroups = this.getPendingItemGroups();
        const pendingSpecialOrders = this.getPendingSpecialOrders();
        const allRunningOrders = this.getAllRunningOrders(tiles, otherOrders);
        const summary = this.getSummary(tiles, pendingItemGroups, allRunningOrders, pendingSpecialOrders);
        const selectedTable = this.findSelectedTable(tiles, otherOrders);

        return (
            <div className="captain-dashboard">

                {/* ── Summary cards ────────────────────────────────────── */}
                <div className="captain-summary-row">
                    {this.renderSummaryCard('pi-calendar', summary.bookingRequests, 'Booking Requests', 'blue')}

                    {/* Pending Items is parked while the QR dispatch flow is
                        reworked, and Pending Orders takes its slot. To restore
                        it, uncomment the line below and the <PendingItemsModal>
                        render further down; its handlers and state are all
                        still in place and untouched.
                    {this.renderSummaryCard('pi-qrcode', summary.pendingItems, 'Pending Items', 'green', this.openPendingItemsModal)}
                    */}
                    {this.renderSummaryCard('pi-check-square', summary.pendingOrders, 'Pending Orders', 'green', this.openPendingOrdersModal)}
                    {this.renderSummaryCard('pi-pencil', summary.ordersInProgress, 'Orders in Progress', 'orange', this.openOrdersModal)}
                    {this.renderSummaryCard('pi-users', summary.totalTables, 'Total Tables', 'purple')}
                </div>

                {/* ── Main content ─────────────────────────────────────── */}
                <div className="captain-content">

                    <div className="captain-left">

                        <div className="captain-toolbar">
                            {/* Takeaway/Delivery has no floor-plan tile to click — just
                                pick the running order straight from this dropdown. */}
                            <Dropdown
                                className="captain-other-order-dropdown"
                                value={selectedOtherOrderId}
                                options={otherOrders.map(o => ({ label: o.label, value: o.requestId }))}
                                onChange={(e) => this.onSelectOtherOrder(e.value)}
                                placeholder="Takeaway / Delivery orders"
                                showClear
                            />

                            <span className="p-input-icon-left captain-search">
                                <i className="pi pi-search" />
                                <InputText
                                    value={search}
                                    placeholder="Search table"
                                    onChange={(e) => this.setState({ search: e.target.value })}
                                />
                            </span>

                            <Button
                                className="p-button-outlined captain-filters-btn"
                                label="Filters"
                                icon="pi pi-sliders-h"
                            />
                        </div>

                        <FloorPlan
                            tables={tiles}
                            selectedTableId={this.state.selectedTableId}
                            onTableSelect={this.onTableSelect}
                        />

                        <div className="captain-action-bar">
                            <Button className="captain-action-btn captain-action-btn--booking" icon="pi pi-calendar" label="Add Booking" />
                            <Button
                                className="captain-action-btn captain-action-btn--qr"
                                icon="pi pi-qrcode"
                                label="New order"
                                onClick={() => this.props.history.push('/pos/')}
                            />
                            <Button
                                className="captain-action-btn captain-action-btn--staff p-button-outlined"
                                icon="pi pi-bell"
                                label="Resource Engagement "
                                onClick={this.openResourceModal}
                            />
                        </div>

                    </div>

                    <div className="captain-right">
                        <ActionPanel
                            table={selectedTable}
                            kotItems={this.getKotItems(selectedTable)}
                            onClose={this.closeDetail}
                            onViewOrder={this.viewOrder}
                            onToggleKotItem={this.toggleKotItemSelected}
                            onMarkKotServed={this.markKotItemsServed}
                            onCloseOrder={this.closeOrder}
                        />
                    </div>

                </div>

                <PendingOrdersModal
                    visible={this.state.pendingOrdersModalOpen}
                    onHide={this.closePendingOrdersModal}
                    groups={pendingSpecialOrders}
                    decidingId={this.state.decidingRequestId}
                    refreshing={this.state.kotRefreshing}
                    error={this.state.decisionError}
                    onApprove={this.approveSpecialOrder}
                    onReject={this.rejectSpecialOrder}
                    onRefresh={this.refreshKotActions}
                />

                {/* Parked alongside its summary card above — see the comment
                    there for how to bring both back.
                <PendingItemsModal
                    visible={pendingItemsModalOpen}
                    onHide={this.closePendingItemsModal}
                    groups={pendingItemGroups}
                    onToggleItem={this.togglePendingItemSelected}
                    onToggleOrder={this.togglePendingItemGroupSelected}
                    onToggleAll={this.toggleAllPendingItemsSelected}
                    onSendToKitchen={this.sendPendingItemsToKitchen}
                    dateRange={this.state.pendingItemsDateRange}
                    onDateRangeChange={this.onPendingItemsDateRangeChange}
                />
                */}

                <ResourceEngagementModal
                    visible={this.state.resourceModalOpen}
                    onHide={this.closeResourceModal}
                    kitchenGroups={this.getLiveKotGroups()}
                    waiterGroups={this.getWaiterAssignments()}
                />

                <OrdersInProgressModal
                    visible={this.state.ordersModalOpen}
                    onHide={this.closeOrdersModal}
                    orders={allRunningOrders}
                    onCloseOrder={this.closeOrder}
                    onViewOrder={this.viewOrder}
                />

            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    loggedUserDetail: state.auth.userObject,
    tables: state.master.masterData.GetresTableList,
    // The action list has its own store slot, kept live by startKotActionsLive.
    tableLists: state.master.kotActions,
    MenuItem: state.keyword.MenuItem !== undefined ? state.keyword.MenuItem : [],
    Waiter: state.keyword.Waiter !== undefined ? state.keyword.Waiter : [],
    Kitchen: state.keyword.Kitchen !== undefined ? state.keyword.Kitchen : [],
});

const mapDispatchToProps = (dispatch) => ({
    getMasterData: (tableUrl, companyId) => dispatch(actions.getMasterData(tableUrl, companyId)),
    getMasterTableList: (table, company) => dispatch(actions.getMasterTableList(table, company)),
    loadKotActions: () => dispatch(actions.loadKotActions()),
    startKotActionsLive: () => dispatch(actions.startKotActionsLive()),
    getMasterKeywordList: (keyword) => dispatch(actions.getMasterKeywordList(keyword)),
    processDynamicData: (model, type, data, func) => dispatch(actions.processDynamicData(model, type, data, func)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CaptainDashboard);
